import { useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Brain, MessageCircle, TrendingUp, User, Clock } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

interface ConversationSession {
  id: string;
  created_at: string;
  start_time: string;
  user_id: string;
  status: number;
  canary_assessment_id: string;
  canary_sentiment: number;
  canary_engagement: number;
  canary_cognitive_load: number;
  canary_emotional_state: string;
  canary_conversation_quality: number;
  canary_alzheimer_category: string;
  canary_depression_overall: string;
  canary_mci_category: string;
  canary_parkinson_category: string;
  canary_energy_dynamics: number;
  canary_energy_overall: number;
  canary_energy_power: number;
  canary_energy_speed: number;
}

// Add a type for sentiment
interface SentimentScore {
  name: string;
  score: number;
}

// Add to ConversationSession
type ConversationSessionWithSentiments = ConversationSession & {
  sentiments: SentimentScore[];
  sleepTime: number; // hours
  heartRate: number; // bpm
  bloodPressureSystolic: number;
  bloodPressureDiastolic: number;
};

const SENTIMENT_NAMES = [
  'Admiration', 'Adoration', 'Aesthetic Appreciation', 'Amusement', 'Anger', 'Anxiety', 'Awe', 'Awkwardness', 'Boredom', 'Calmness', 'Concentration', 'Confusion', 'Contemplation', 'Contempt', 'Contentment', 'Craving', 'Desire', 'Determination', 'Disappointment', 'Disgust', 'Distress', 'Doubt', 'Ecstasy', 'Embarrassment', 'Empathic Pain', 'Entrancement', 'Envy', 'Excitement', 'Fear', 'Guilt', 'Horror', 'Interest', 'Joy', 'Love', 'Nostalgia', 'Pain', 'Pride', 'Realization', 'Relief', 'Romance', 'Sadness', 'Satisfaction', 'Shame', 'Surprise (negative)', 'Surprise (positive)', 'Sympathy', 'Tiredness', 'Triumph'
];

// Helper to generate mock sentiment scores
function generateMockSentiments(type: 'positive' | 'negative' | 'neutral'): SentimentScore[] {
  return SENTIMENT_NAMES.map(name => {
    let base = 0.08 + Math.random() * 0.07; // Most are in the middle
    if (type === 'positive') {
      if ([
        'Joy', 'Satisfaction', 'Calmness', 'Relief', 'Admiration', 'Amusement', 'Excitement', 'Contentment', 'Love', 'Triumph', 'Aesthetic Appreciation', 'Pride', 'Surprise (positive)'
      ].includes(name)) {
        base = 0.18 + Math.random() * 0.12; // Higher for positive
      } else if ([
        'Anger', 'Anxiety', 'Distress', 'Disappointment', 'Fear', 'Pain', 'Sadness', 'Shame', 'Contempt', 'Disgust', 'Boredom', 'Tiredness', 'Surprise (negative)'
      ].includes(name)) {
        base = 0.01 + Math.random() * 0.04; // Lower for negative
      }
    } else if (type === 'negative') {
      if ([
        'Anger', 'Anxiety', 'Distress', 'Disappointment', 'Fear', 'Pain', 'Sadness', 'Shame', 'Contempt', 'Disgust', 'Boredom', 'Tiredness', 'Surprise (negative)'
      ].includes(name)) {
        base = 0.18 + Math.random() * 0.12; // Higher for negative
      } else if ([
        'Joy', 'Satisfaction', 'Calmness', 'Relief', 'Admiration', 'Amusement', 'Excitement', 'Contentment', 'Love', 'Triumph', 'Aesthetic Appreciation', 'Pride', 'Surprise (positive)'
      ].includes(name)) {
        base = 0.01 + Math.random() * 0.04; // Lower for positive
      }
    }
    return { name, score: parseFloat(base.toFixed(3)) };
  });
}

// Function to generate mock data showing decline over a year, now with sentiments
const generateMockData = (): ConversationSessionWithSentiments[] => {
  const sessions: ConversationSessionWithSentiments[] = [];
  const startDate = new Date();
  startDate.setFullYear(startDate.getFullYear() - 1); // Start from 1 year ago
  let lastPlateauSession: ConversationSessionWithSentiments | null = null;
  for (let i = 0; i < 12; i++) {
    const date = new Date(startDate);
    date.setMonth(startDate.getMonth() + i);
    let session: ConversationSessionWithSentiments;
    let sentiments: SentimentScore[];
    if (i === 3) {
      sentiments = generateMockSentiments('negative'); // Bad day
    } else if (i === 7) {
      sentiments = generateMockSentiments('positive'); // Good day
    } else {
      sentiments = generateMockSentiments('neutral');
    }
    // Sleep, HR, BP: add some trend and randomness
    let sleepTime = 7.5 - i * 0.08 + (Math.random() - 0.5) * 0.4; // 7.5h, slight decline
    let heartRate = 68 + i * 0.7 + (Math.random() - 0.5) * 2; // 68bpm, slight increase
    let bloodPressureSystolic = 120 + Math.sin(i / 2) * 4 + (Math.random() - 0.5) * 3; // 120, fluctuates
    let bloodPressureDiastolic = 78 + Math.cos(i / 2) * 2 + (Math.random() - 0.5) * 2; // 78, fluctuates
    sleepTime = Math.max(5.5, Math.min(8.5, sleepTime));
    heartRate = Math.max(60, Math.min(90, heartRate));
    bloodPressureSystolic = Math.round(Math.max(105, Math.min(140, bloodPressureSystolic)));
    bloodPressureDiastolic = Math.round(Math.max(60, Math.min(95, bloodPressureDiastolic)));
    if (i < 9) {
      const declineFactor = 1 - (i / 12);
      const baseEnergy = 90 * declineFactor + Math.floor(Math.random() * 13) - 6;
      const baseCognitive = 85 * declineFactor + Math.floor(Math.random() * 13) - 6;
      session = {
        id: `session-${i}`,
        created_at: date.toISOString(),
        start_time: date.toISOString(),
        user_id: 'mock-user',
        status: 3,
        canary_assessment_id: `assessment-${i}`,
        canary_sentiment: Math.max(40, baseEnergy * 0.8 + Math.floor(Math.random() * 9) - 4),
        canary_engagement: Math.max(45, baseEnergy * 0.85 + Math.floor(Math.random() * 9) - 4),
        canary_cognitive_load: Math.max(50, baseCognitive * 0.9 + Math.floor(Math.random() * 9) - 4),
        canary_emotional_state: 'neutral',
        canary_conversation_quality: Math.max(60, baseEnergy * 0.95 + Math.floor(Math.random() * 9) - 4),
        canary_alzheimer_category: baseCognitive > 80 ? 'LOW' : baseCognitive > 50 ? 'MEDIUM' : 'HIGH',
        canary_depression_overall: baseCognitive * 0.9 > 80 ? 'LOW' : baseCognitive * 0.9 > 50 ? 'MEDIUM' : 'HIGH',
        canary_mci_category: baseCognitive * 0.95 > 80 ? 'LOW' : baseCognitive * 0.95 > 50 ? 'MEDIUM' : 'HIGH',
        canary_parkinson_category: baseCognitive * 0.85 > 80 ? 'LOW' : baseCognitive * 0.85 > 50 ? 'MEDIUM' : 'HIGH',
        canary_energy_dynamics: Math.max(50, baseEnergy * 0.9 + Math.floor(Math.random() * 9) - 4),
        canary_energy_overall: Math.max(55, baseEnergy + Math.floor(Math.random() * 9) - 4),
        canary_energy_power: Math.max(45, baseEnergy * 0.85 + Math.floor(Math.random() * 9) - 4),
        canary_energy_speed: Math.max(40, baseEnergy * 0.8 + Math.floor(Math.random() * 9) - 4),
        sentiments,
        sleepTime,
        heartRate: Math.round(heartRate),
        bloodPressureSystolic,
        bloodPressureDiastolic,
      };
      if (i === 8) lastPlateauSession = { ...session };
    } else {
      session = {
        ...lastPlateauSession!,
        id: `session-${i}`,
        created_at: date.toISOString(),
        start_time: date.toISOString(),
        canary_assessment_id: `assessment-${i}`,
        sentiments,
        sleepTime,
        heartRate: Math.round(heartRate),
        bloodPressureSystolic,
        bloodPressureDiastolic,
      };
    }
    sessions.push(session);
  }
  return sessions;
};

export default function UserDashboard() {
  const [sessions, setSessions] = useState<ConversationSessionWithSentiments[]>([]);
  const [userName, setUserName] = useState<string>('Johnny');
  const [loading, setLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [expandedSession, setExpandedSession] = useState<string | null>(null);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    // Replace Supabase fetch with mock data
    const mockData = generateMockData();
    setSessions(mockData);
    setLoading(false);
  }, []);

  const getStatusText = (status: number | string) => {
    // Convert to number if it's a string
    const statusNum = typeof status === 'string' ? parseInt(status) : status;
    
    switch (statusNum) {
      case 0:
        return 'In Progress';
      case 1:
      case 2:
        return 'Processing';
      case 3:
        return 'Results Ready';
      default:
        console.log('Unknown status:', status, typeof status); // Debug log
        return 'Unknown';
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen">Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center gap-4 mb-4">
          <Clock className="w-5 h-5 text-purple-500" />
          <span className="text-sm text-gray-600">
            {currentTime.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
          </span>
        </div>

        <div className="mb-8">
          <h1 className="text-4xl font-bold text-purple-600">
            Hello, {userName}
          </h1>
          <p className="mt-2 text-gray-600">
            We're here to support your journey to better health and wellness. Your daily care companion is ready to assist you with everything you need.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card className="p-6 bg-white shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-purple-100 rounded-full">
                <MessageCircle className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Today's Tasks</p>
                <p className="text-2xl font-bold">{sessions.length} remaining</p>
              </div>
            </div>
          </Card>

          <Card className="p-6 bg-white shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-purple-100 rounded-full">
                <Clock className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Next Appointment</p>
                <p className="text-2xl font-bold">Tomorrow, 2:30 PM</p>
              </div>
            </div>
          </Card>

          <Card className="p-6 bg-white shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-purple-100 rounded-full">
                <TrendingUp className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Wellness Score</p>
                <p className="text-2xl font-bold">92%</p>
              </div>
            </div>
          </Card>
        </div>

        <Tabs defaultValue="sessions" className="space-y-6">
          <TabsList className="w-full bg-purple-100 p-2 rounded-lg">
            <TabsTrigger 
              value="sessions" 
              className="w-1/2 py-3 text-lg font-semibold data-[state=active]:bg-purple-600 data-[state=active]:text-white transition-all"
            >
              Recent Sessions
            </TabsTrigger>
            <TabsTrigger 
              value="trends" 
              className="w-1/2 py-3 text-lg font-semibold data-[state=active]:bg-purple-600 data-[state=active]:text-white transition-all"
            >
              Trends
            </TabsTrigger>
          </TabsList>

          <TabsContent value="sessions">
            <div className="grid gap-6">
              {[...sessions].map((session) => (
                <Card key={session.id} className="p-6 bg-white shadow-sm">
                  <div className="space-y-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="font-semibold">
                          Session from {new Date(session.start_time).toLocaleDateString()}
                        </h3>
                        <p className="text-sm text-gray-500">
                          {new Date(session.start_time).toLocaleTimeString()}
                        </p>
                        <p className="text-sm text-purple-600 mt-1">
                          Status: {getStatusText(session.status)}
                        </p>
                        <p className="text-sm text-gray-600 mt-1">
                          Speech Processing ID: {session.canary_assessment_id}
                        </p>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                       <div>
                        <p className="text-sm text-gray-600">MCI Risk:</p>
                        <p className="text-lg font-semibold">{session.canary_mci_category}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Alzheimer's Risk:</p>
                        <p className="text-lg font-semibold">{session.canary_alzheimer_category}</p>
                      </div>
                      {/* <div>
                        <p className="text-sm text-gray-600">Anxiety Risk:</p>
                        <p className="text-lg font-semibold uppercase">{session.canary_anxiety_overall}</p>
                      </div> */}
                      <div>
                        <p className="text-sm text-gray-600">Depression Risk:</p>
                        <p className="text-lg font-semibold uppercase">{session.canary_depression_overall}</p>
                      </div>
                      
                      <div>
                        <p className="text-sm text-gray-600">Parkinson's Risk:</p>
                        <p className="text-lg font-semibold">{session.canary_parkinson_category}</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <p className="text-sm text-gray-600">Energy Overall:</p>
                        <p className="text-lg font-semibold">{session.canary_energy_overall}%</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Energy Dynamics:</p>
                        <p className="text-lg font-semibold">{session.canary_energy_dynamics}%</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Energy Power:</p>
                        <p className="text-lg font-semibold">{session.canary_energy_power}%</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Energy Speed:</p>
                        <p className="text-lg font-semibold">{session.canary_energy_speed}%</p>
                      </div>
                    </div>

                    {/* Sentiment dropdown */}
                    <div>
                      <button
                        className="mt-4 text-sm text-purple-700 underline"
                        onClick={() => setExpandedSession(expandedSession === session.id ? null : session.id)}
                      >
                        {expandedSession === session.id ? 'Hide Sentiments' : 'Show Sentiments'}
                      </button>
                      {expandedSession === session.id && (
                        <div className="mt-4 bg-purple-50 rounded p-4 max-h-64 overflow-y-auto">
                          <h4 className="font-semibold mb-2">Sentiment Scores</h4>
                          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                            {session.sentiments.map(s => (
                              <div key={s.name} className="flex justify-between text-sm">
                                <span className="text-gray-700">{s.name}</span>
                                <span className="font-mono text-purple-700">{(s.score * 100).toFixed(1)}%</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="trends">
            <div className="space-y-6">
              <Card className="p-6">
                <h3 className="text-xl font-semibold mb-6">Energy Metrics Over Time</h3>
                <div className="h-[400px] w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                      data={[...sessions].map(session => ({
                        date: new Date(session.start_time).toLocaleDateString(),
                        overall: session.canary_energy_overall,
                        dynamics: session.canary_energy_dynamics,
                        power: session.canary_energy_power,
                        speed: session.canary_energy_speed,
                      }))}
                      margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Line 
                        type="monotone" 
                        dataKey="overall" 
                        stroke="#8884d8" 
                        name="Energy Overall" 
                        strokeWidth={2}
                        dot={{ r: 6, fill: "#8884d8" }}
                        activeDot={{ r: 8, fill: "#8884d8" }}
                        connectNulls
                      />
                      <Line 
                        type="monotone" 
                        dataKey="dynamics" 
                        stroke="#82ca9d" 
                        name="Energy Dynamics" 
                        strokeWidth={2}
                        dot={{ r: 6, fill: "#82ca9d" }}
                        activeDot={{ r: 8, fill: "#82ca9d" }}
                        connectNulls
                      />
                      <Line 
                        type="monotone" 
                        dataKey="power" 
                        stroke="#ffc658" 
                        name="Energy Power" 
                        strokeWidth={2}
                        dot={{ r: 6, fill: "#ffc658" }}
                        activeDot={{ r: 8, fill: "#ffc658" }}
                        connectNulls
                      />
                      <Line 
                        type="monotone" 
                        dataKey="speed" 
                        stroke="#ff7300" 
                        name="Energy Speed" 
                        strokeWidth={2}
                        dot={{ r: 6, fill: "#ff7300" }}
                        activeDot={{ r: 8, fill: "#ff7300" }}
                        connectNulls
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </Card>

              <Card className="p-6">
                <h3 className="text-xl font-semibold mb-6">Cognitive Health Indicators</h3>
                <div className="h-[400px] w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                      data={[...sessions].map(session => ({
                        date: new Date(session.start_time).toLocaleDateString(),
                        mci: session.canary_mci_category === 'HIGH' ? 3 : 
                             session.canary_mci_category === 'MEDIUM' ? 2 : 1,
                        alzheimers: session.canary_alzheimer_category === 'HIGH' ? 3 : 
                                   session.canary_alzheimer_category === 'MEDIUM' ? 2 : 1,
                        depression: session.canary_depression_overall === 'HIGH' ? 3 : 
                                  session.canary_depression_overall === 'MEDIUM' ? 2 : 1,
                        parkinsons: session.canary_parkinson_category === 'HIGH' ? 3 : 
                                   session.canary_parkinson_category === 'MEDIUM' ? 2 : 1,
                      }))}
                      margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis 
                        ticks={[1, 2, 3]} 
                        domain={[1, 3]} 
                        tickFormatter={(value) => {
                          return value === 1 ? 'LOW' : value === 2 ? 'MEDIUM' : 'HIGH';
                        }}
                      />
                      <Tooltip 
                        formatter={(value: number) => {
                          return value === 1 ? 'LOW' : value === 2 ? 'MEDIUM' : 'HIGH';
                        }}
                      />
                      <Legend />
                      <Line 
                        type="monotone" 
                        dataKey="mci" 
                        stroke="#8884d8" 
                        name="MCI Risk" 
                        strokeWidth={2}
                        dot={{ r: 6, fill: "#8884d8" }}
                        activeDot={{ r: 8, fill: "#8884d8" }}
                        connectNulls
                      />
                      <Line 
                        type="monotone" 
                        dataKey="alzheimers" 
                        stroke="#82ca9d" 
                        name="Alzheimer's Risk" 
                        strokeWidth={2}
                        dot={{ r: 6, fill: "#82ca9d" }}
                        activeDot={{ r: 8, fill: "#82ca9d" }}
                        connectNulls
                      />
                      <Line 
                        type="monotone" 
                        dataKey="depression" 
                        stroke="#ffc658" 
                        name="Depression Risk" 
                        strokeWidth={2}
                        dot={{ r: 6, fill: "#ffc658" }}
                        activeDot={{ r: 8, fill: "#ffc658" }}
                        connectNulls
                      />
                      <Line 
                        type="monotone" 
                        dataKey="parkinsons" 
                        stroke="#ff7300" 
                        name="Parkinson's Risk" 
                        strokeWidth={2}
                        dot={{ r: 6, fill: "#ff7300" }}
                        activeDot={{ r: 8, fill: "#ff7300" }}
                        connectNulls
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </Card>

              <Card className="p-6">
                <h3 className="text-xl font-semibold mb-6">Sentiment Trends</h3>
                <div className="h-[400px] w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                      data={sessions.map(session => {
                        // Pick a few key sentiments to trend
                        const get = (name: string) => session.sentiments.find(s => s.name === name)?.score ?? 0;
                        return {
                          date: new Date(session.start_time).toLocaleDateString(),
                          Anger: get('Anger'),
                          Anxiety: get('Anxiety'),
                          Distress: get('Distress'),
                          Joy: get('Joy'),
                          Satisfaction: get('Satisfaction'),
                        };
                      })}
                      margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis domain={[0, 0.3]} tickFormatter={v => `${Math.round(v * 100)}%`} />
                      <Tooltip formatter={(v: number) => `${(v * 100).toFixed(1)}%`} />
                      <Legend />
                      <Line type="monotone" dataKey="Anger" stroke="#e57373" name="Anger" strokeWidth={2} dot={{ r: 6, fill: "#e57373" }} activeDot={{ r: 8, fill: "#e57373" }} connectNulls />
                      <Line type="monotone" dataKey="Anxiety" stroke="#ba68c8" name="Anxiety" strokeWidth={2} dot={{ r: 6, fill: "#ba68c8" }} activeDot={{ r: 8, fill: "#ba68c8" }} connectNulls />
                      <Line type="monotone" dataKey="Distress" stroke="#ffb74d" name="Distress" strokeWidth={2} dot={{ r: 6, fill: "#ffb74d" }} activeDot={{ r: 8, fill: "#ffb74d" }} connectNulls />
                      <Line type="monotone" dataKey="Joy" stroke="#64b5f6" name="Joy" strokeWidth={2} dot={{ r: 6, fill: "#64b5f6" }} activeDot={{ r: 8, fill: "#64b5f6" }} connectNulls />
                      <Line type="monotone" dataKey="Satisfaction" stroke="#81c784" name="Satisfaction" strokeWidth={2} dot={{ r: 6, fill: "#81c784" }} activeDot={{ r: 8, fill: "#81c784" }} connectNulls />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </Card>
              {/* Sleep Time Trend */}
              <Card className="p-6">
                <h3 className="text-xl font-semibold mb-6">Sleep Time (hours)</h3>
                <div className="h-[300px] w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                      data={sessions.map(session => ({
                        date: new Date(session.start_time).toLocaleDateString(),
                        sleepTime: session.sleepTime,
                      }))}
                      margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis domain={[5, 9]} tickFormatter={v => `${v}h`} />
                      <Tooltip formatter={(v: number) => `${v.toFixed(2)}h`} />
                      <Legend />
                      <Line type="monotone" dataKey="sleepTime" stroke="#7e57c2" name="Sleep Time" strokeWidth={2} dot={{ r: 6, fill: "#7e57c2" }} activeDot={{ r: 8, fill: "#7e57c2" }} connectNulls />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </Card>
              {/* Heart Rate Trend */}
              <Card className="p-6">
                <h3 className="text-xl font-semibold mb-6">Heart Rate (bpm)</h3>
                <div className="h-[300px] w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                      data={sessions.map(session => ({
                        date: new Date(session.start_time).toLocaleDateString(),
                        heartRate: session.heartRate,
                      }))}
                      margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis domain={[60, 95]} tickFormatter={v => `${v} bpm`} />
                      <Tooltip formatter={(v: number) => `${v} bpm`} />
                      <Legend />
                      <Line type="monotone" dataKey="heartRate" stroke="#ef5350" name="Heart Rate" strokeWidth={2} dot={{ r: 6, fill: "#ef5350" }} activeDot={{ r: 8, fill: "#ef5350" }} connectNulls />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </Card>
              {/* Blood Pressure Trend */}
              <Card className="p-6">
                <h3 className="text-xl font-semibold mb-6">Blood Pressure (mmHg)</h3>
                <div className="h-[300px] w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                      data={sessions.map(session => ({
                        date: new Date(session.start_time).toLocaleDateString(),
                        systolic: session.bloodPressureSystolic,
                        diastolic: session.bloodPressureDiastolic,
                      }))}
                      margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis domain={[60, 150]} tickFormatter={v => `${v} mmHg`} />
                      <Tooltip formatter={(v: number) => `${v} mmHg`} />
                      <Legend />
                      <Line type="monotone" dataKey="systolic" stroke="#42a5f5" name="Systolic" strokeWidth={2} dot={{ r: 6, fill: "#42a5f5" }} activeDot={{ r: 8, fill: "#42a5f5" }} connectNulls />
                      <Line type="monotone" dataKey="diastolic" stroke="#66bb6a" name="Diastolic" strokeWidth={2} dot={{ r: 6, fill: "#66bb6a" }} activeDot={{ r: 8, fill: "#66bb6a" }} connectNulls />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
} 